import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  startAfter,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Session } from '../../types/models';
import { GSTCalculator } from '../../lib/calculateGST';
import { assertWriteAccess } from '../../lib/telemetry';

interface SessionState {
  sessions: Session[];
  currentSession: Partial<Session> | null;
  isLoading: boolean;
  error: string | null;
  totalEarnings: number;
  totalGSTCollected: number;
  totalKmDriven: number;
  isSessionActive: boolean;
  paging: {
    nextCursor: { startTimeMs: number; endTimeMs: number | null } | null;
  };
}

const initialState: SessionState = {
  sessions: [],
  currentSession: null,
  isLoading: false,
  error: null,
  totalEarnings: 0,
  totalGSTCollected: 0,
  totalKmDriven: 0,
  isSessionActive: false,
  paging: {
    nextCursor: null,
  },
};

// Async thunks
export const fetchSessions = createAsyncThunk(
  'sessions/fetchSessions',
  async (userId: string, { rejectWithValue }) => {
    try {
      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const q = query(sessionsRef, orderBy('startTime', 'desc'));
      const querySnapshot = await getDocs(q);

      const sessions: Session[] = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          ...data,
          startTime: data.startTime,
          endTime: data.endTime,
        } as Session);
      });

      return sessions;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Paged fetch using Firestore cursors (order by startTime desc, endTime desc)
export const fetchSessionsPaged = createAsyncThunk(
  'sessions/fetchSessionsPaged',
  async (
    {
      userId,
      pageSize = 10,
      after,
    }: {
      userId: string;
      pageSize?: number;
      after?: { startTimeMs: number; endTimeMs: number | null };
    },
    { rejectWithValue }
  ) => {
    try {
      const sessionsRef = collection(db, 'users', userId, 'sessions');
      let qBase = query(
        sessionsRef,
        orderBy('startTime', 'desc'),
        orderBy('endTime', 'desc'),
        limit(pageSize + 1)
      );

      if (after) {
        const startTs = Timestamp.fromMillis(after.startTimeMs);
        const endTs = after.endTimeMs ? Timestamp.fromMillis(after.endTimeMs) : startTs;
        qBase = query(
          sessionsRef,
          orderBy('startTime', 'desc'),
          orderBy('endTime', 'desc'),
          startAfter(startTs, endTs),
          limit(pageSize + 1)
        );
      }

      const snapshot = await getDocs(qBase);
      const docs = snapshot.docs;
      const hasExtra = docs.length > pageSize;
      const pageDocs = hasExtra ? docs.slice(0, pageSize) : docs;

      const items: Session[] = pageDocs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          startTime: data.startTime,
          endTime: data.endTime,
        } as Session;
      });

      let nextCursor: { startTimeMs: number; endTimeMs: number | null } | null = null;
      if (hasExtra) {
        const last = pageDocs[pageDocs.length - 1];
        const lastData = last.data();
        nextCursor = {
          startTimeMs: lastData.startTime.toMillis(),
          endTimeMs: lastData.endTime ? lastData.endTime.toMillis() : null,
        };
      }

      return { items, nextCursor };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const startSession = createAsyncThunk(
  'sessions/startSession',
  async (
    { userId, startOdometer }: { userId: string; startOdometer: number },
    { rejectWithValue }
  ) => {
    try {
      const sessionData = {
        userId,
        startOdometer,
        endOdometer: 0,
        totalKmDriven: 0,
        totalKmWithPassenger: 0,
        grossEarnings: 0,
        gstCollected: 0,
        expensesLinked: [],
        startTime: Timestamp.now(),
        endTime: Timestamp.now(), // Will be updated when session ends
      };

      return sessionData;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const endSession = createAsyncThunk(
  'sessions/endSession',
  async (
    {
      userId,
      endOdometer,
      totalKmWithPassenger,
      grossEarnings,
      provinceCode,
      drivingDurationMinutes,
    }: {
      userId: string;
      endOdometer: number;
      totalKmWithPassenger: number;
      grossEarnings: number;
      provinceCode: string;
      drivingDurationMinutes?: number;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { sessions: SessionState };
      const currentSession = state.sessions.currentSession;

      if (!currentSession || !currentSession.startOdometer) {
        throw new Error('No active session found');
      }

      const totalKmDriven = endOdometer - currentSession.startOdometer;

      // Calculate GST collected
      const calculator = new GSTCalculator(provinceCode);
      const gstCollected = calculator.calculateGSTFromPreTax(grossEarnings);

      const completedSession = {
        ...currentSession,
        endOdometer,
        totalKmDriven,
        totalKmWithPassenger,
        grossEarnings,
        gstCollected,
        endTime: Timestamp.now(),
        ...(drivingDurationMinutes && { drivingDurationMinutes }),
      };

      // Save to Firestore
      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const docRef = await addDoc(sessionsRef, completedSession);

      return {
        id: docRef.id,
        ...completedSession,
      } as Session;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSession = createAsyncThunk(
  'sessions/updateSession',
  async (
    {
      userId,
      sessionId,
      updates,
      provinceCode,
    }: {
      userId: string;
      sessionId: string;
      updates: Partial<Session>;
      provinceCode: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Assert write access with fresh token
      await assertWriteAccess();

      const state = getState() as { sessions: SessionState };
      const existingSession = state.sessions.sessions.find(s => s.id === sessionId);

      if (!existingSession) {
        throw new Error('Session not found');
      }

      // Recalculate GST if earnings changed
      let gstCollected = existingSession.gstCollected;
      if (updates.grossEarnings !== undefined) {
        const calculator = new GSTCalculator(provinceCode);
        gstCollected = calculator.calculateGSTFromPreTax(updates.grossEarnings);
      }

      // Recalculate total km if odometer readings changed
      let totalKmDriven = existingSession.totalKmDriven;
      if (updates.startOdometer !== undefined || updates.endOdometer !== undefined) {
        const startOdo = updates.startOdometer ?? existingSession.startOdometer;
        const endOdo = updates.endOdometer ?? existingSession.endOdometer;
        totalKmDriven = endOdo - startOdo;
      }

      const updatedData = {
        ...updates,
        gstCollected,
        totalKmDriven,
        updatedAt: Timestamp.now(),
      };

      const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
      await updateDoc(sessionRef, updatedData);

      return {
        ...existingSession,
        ...updatedData,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSession = createAsyncThunk(
  'sessions/deleteSession',
  async ({ userId, sessionId }: { userId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      // Assert write access with fresh token
      await assertWriteAccess();

      const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
      await deleteDoc(sessionRef);

      return sessionId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const linkExpenseToSession = createAsyncThunk(
  'sessions/linkExpenseToSession',
  async (
    { userId, sessionId, expenseId }: { userId: string; sessionId: string; expenseId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { sessions: SessionState };
      const session = state.sessions.sessions.find(s => s.id === sessionId);

      if (!session) {
        throw new Error('Session not found');
      }

      const updatedExpenses = [...session.expensesLinked];
      if (!updatedExpenses.includes(expenseId)) {
        updatedExpenses.push(expenseId);
      }

      const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
      await updateDoc(sessionRef, { expensesLinked: updatedExpenses });

      return { sessionId, expenseId };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const sessionSlice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    cancelCurrentSession: state => {
      state.currentSession = null;
      state.isSessionActive = false;
    },
    updateCurrentSession: (state, action: PayloadAction<Partial<Session>>) => {
      if (state.currentSession) {
        state.currentSession = { ...state.currentSession, ...action.payload };
      }
    },
    upsertSessionLocal: (state, action: PayloadAction<Session>) => {
      const item = action.payload;
      const index = state.sessions.findIndex(s => s.id === item.id);
      if (index >= 0) {
        state.sessions[index] = item;
      } else {
        state.sessions.unshift(item);
        if (state.sessions.length > 10) state.sessions = state.sessions.slice(0, 10);
      }
      // Deterministic sort: startTime/date desc then createdAt/endTime desc
      state.sessions = [...state.sessions].sort((a: any, b: any) => {
        const ad = a.date?.toMillis?.() ?? a.startTime?.toMillis?.() ?? 0;
        const bd = b.date?.toMillis?.() ?? b.startTime?.toMillis?.() ?? 0;
        if (bd !== ad) return bd - ad;
        const a2 = a.createdAt?.toMillis?.() ?? a.endTime?.toMillis?.() ?? 0;
        const b2 = b.createdAt?.toMillis?.() ?? b.endTime?.toMillis?.() ?? 0;
        return b2 - a2;
      });
    },
    recomputeSessionAggregates: state => {
      state.totalEarnings = state.sessions.reduce((sum, s) => sum + s.grossEarnings, 0);
      state.totalGSTCollected = state.sessions.reduce((sum, s) => sum + s.gstCollected, 0);
      state.totalKmDriven = state.sessions.reduce((sum, s) => sum + s.totalKmDriven, 0);
    },
  },
  extraReducers: builder => {
    builder
      // Fetch sessions
      .addCase(fetchSessions.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions = action.payload;
        state.totalEarnings = action.payload.reduce(
          (sum, session) => sum + session.grossEarnings,
          0
        );
        state.totalGSTCollected = action.payload.reduce(
          (sum, session) => sum + session.gstCollected,
          0
        );
        state.totalKmDriven = action.payload.reduce(
          (sum, session) => sum + session.totalKmDriven,
          0
        );
      })
      // Paged fetch
      .addCase(fetchSessionsPaged.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSessionsPaged.fulfilled, (state, action) => {
        state.isLoading = false;
        const { items, nextCursor } = action.payload as any;
        state.sessions = items;
        state.paging.nextCursor = nextCursor;
        state.totalEarnings = items.reduce((sum: number, s: Session) => sum + s.grossEarnings, 0);
        state.totalGSTCollected = items.reduce(
          (sum: number, s: Session) => sum + s.gstCollected,
          0
        );
        state.totalKmDriven = items.reduce((sum: number, s: Session) => sum + s.totalKmDriven, 0);
      })
      .addCase(fetchSessionsPaged.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Start session
      .addCase(startSession.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSession = action.payload;
        state.isSessionActive = true;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // End session
      .addCase(endSession.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(endSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions.unshift(action.payload);
        state.currentSession = null;
        state.isSessionActive = false;
        state.totalEarnings += action.payload.grossEarnings;
        state.totalGSTCollected += action.payload.gstCollected;
        state.totalKmDriven += action.payload.totalKmDriven;
      })
      .addCase(endSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update session
      .addCase(updateSession.fulfilled, (state, action) => {
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          const oldSession = state.sessions[index];
          state.sessions[index] = action.payload;

          // Update totals
          state.totalEarnings =
            state.totalEarnings - oldSession.grossEarnings + action.payload.grossEarnings;
          state.totalGSTCollected =
            state.totalGSTCollected - oldSession.gstCollected + action.payload.gstCollected;
          state.totalKmDriven =
            state.totalKmDriven - oldSession.totalKmDriven + action.payload.totalKmDriven;
        }
      })

      // Delete session
      .addCase(deleteSession.fulfilled, (state, action) => {
        const sessionIndex = state.sessions.findIndex(s => s.id === action.payload);
        if (sessionIndex !== -1) {
          const deletedSession = state.sessions[sessionIndex];
          state.sessions.splice(sessionIndex, 1);
          state.totalEarnings -= deletedSession.grossEarnings;
          state.totalGSTCollected -= deletedSession.gstCollected;
          state.totalKmDriven -= deletedSession.totalKmDriven;
        }
      })

      // Link expense to session
      .addCase(linkExpenseToSession.fulfilled, (state, action) => {
        const session = state.sessions.find(s => s.id === action.payload.sessionId);
        if (session && !session.expensesLinked.includes(action.payload.expenseId)) {
          session.expensesLinked.push(action.payload.expenseId);
        }
      });
  },
});

export const {
  clearError,
  cancelCurrentSession,
  updateCurrentSession,
  upsertSessionLocal,
  recomputeSessionAggregates,
} = sessionSlice.actions;
export default sessionSlice.reducer;
